import { Component, inject, input } from '@angular/core';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { LocalizationService } from '../../../core/localization/localization.service';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import { FormDraft } from './ai-authoring.models';
import { flattenFormDraftFields } from './ai-authoring.util';

@Component({
  standalone: true,
  selector: 'ain-form-draft-preview',
  imports: [TableModule, TagModule, InfoHintComponent],
  template: `
    <section class="form-draft-preview">
      <ain-info-hint
        [titleKey]="'dynamic.aiAuthoring.formPreview.infoTitle'"
        [descriptionKey]="'dynamic.aiAuthoring.formPreview.infoDescription'"
        [bulletKeys]="[
          'dynamic.aiAuthoring.formPreview.infoBulletFields',
          'dynamic.aiAuthoring.formPreview.infoBulletBuilder'
        ]" />

      @if (usedFallback()) {
        <div class="banner banner-warn">
          <strong>{{ t('dynamic.aiAuthoring.fallbackDisclaimer') }}</strong>
        </div>
      }

      @if (warnings().length) {
        <div class="banner banner-info">
          <strong>{{ t('dynamic.aiAuthoring.warningsTitle') }}</strong>
          <ul>
            @for (warning of warnings(); track warning) {
              <li>{{ warning }}</li>
            }
          </ul>
        </div>
      }

      <div class="meta-row">
        <span>{{ t('dynamic.aiAuthoring.formPreview.layout') }}: <strong>{{ draft().layoutType }}</strong></span>
        @if (draft().targetEntityKey) {
          <span>{{ t('dynamic.aiAuthoring.formPreview.targetEntity') }}: <strong>{{ draft().targetEntityKey }}</strong></span>
        }
        <span>{{ t('dynamic.aiAuthoring.formPreview.sectionCount') }}: <strong>{{ draft().sections.length }}</strong></span>
      </div>

      <p-table [value]="previewFields()" dataKey="key">
        <ng-template pTemplate="header">
          <tr>
            <th>{{ t('dynamic.aiAuthoring.formPreview.fieldKey') }}</th>
            <th>{{ t('dynamic.aiAuthoring.formPreview.fieldType') }}</th>
            <th>{{ t('dynamic.aiAuthoring.formPreview.fieldLabel') }}</th>
            <th>{{ t('dynamic.aiAuthoring.formPreview.fieldRequired') }}</th>
            <th>{{ t('dynamic.aiAuthoring.formPreview.optionsCount') }}</th>
            <th>{{ t('dynamic.aiAuthoring.formPreview.section') }}</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-field>
          <tr>
            <td><code>{{ field.key }}</code></td>
            <td>{{ field.type }}</td>
            <td>{{ field.label }}</td>
            <td>
              <p-tag
                [severity]="field.required ? 'danger' : 'secondary'"
                [value]="field.required ? t('common.yes') : t('common.no')" />
            </td>
            <td>{{ field.optionsCount }}</td>
            <td>{{ field.sectionTitle }}</td>
          </tr>
        </ng-template>
      </p-table>
    </section>
  `,
  styles: [`
    .form-draft-preview { display: grid; gap: 16px; }
    .banner {
      border-radius: 10px;
      padding: 12px 14px;
      display: grid;
      gap: 6px;
    }
    .banner-warn { background: #fff7ed; border: 1px solid #fdba74; color: #9a3412; }
    .banner-info { background: #eff6ff; border: 1px solid #93c5fd; color: #1e3a8a; }
    .banner ul { margin: 0; padding-inline-start: 1.2rem; }
    .meta-row {
      display: flex;
      gap: 16px;
      flex-wrap: wrap;
      color: #374151;
    }
  `]
})
export class FormDraftPreviewComponent {
  private readonly localization = inject(LocalizationService);

  readonly draft = input.required<FormDraft>();
  readonly warnings = input<string[]>([]);
  readonly usedFallback = input(false);

  previewFields() {
    return flattenFormDraftFields(this.draft());
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
